import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { UPDATE_HISTORY, APP_VERSION } from '../../constants/appConfig';

const UpdatesPopover = () => {
  
  const updates = UPDATE_HISTORY;

  const [showPopover, setShowPopover] = useState(() => {
    const lastSeenVersion = localStorage.getItem('lastSeenUpdateVersion');
    const lastViewedVersion = localStorage.getItem('lastViewedUpdateVersion');
    
    const latestVersion = updates[0]?.version;
    
    return latestVersion && (!lastSeenVersion || lastSeenVersion !== latestVersion);
  });

  const [unreadCount, setUnreadCount] = useState(0);

  const handleClose = () => {
    setShowPopover(false);
    if (updates.length > 0) {
      const latestVersion = updates[0].version;
      localStorage.setItem('lastSeenUpdateVersion', latestVersion);
      localStorage.setItem('lastViewedUpdateVersion', latestVersion);
      setUnreadCount(0);
    }
  };

  const handleViewUpdates = () => {
    setShowPopover(true);
    if (updates.length > 0) {
      const latestVersion = updates[0].version;
      localStorage.setItem('lastViewedUpdateVersion', latestVersion);
      localStorage.setItem('lastSeenUpdateVersion', latestVersion);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    const lastViewedVersion = localStorage.getItem('lastViewedUpdateVersion');
    if (!lastViewedVersion) {
      setUnreadCount(updates.length);
      return;
    }

    const unreadUpdates = updates.filter(
      update => !lastViewedVersion || update.version > lastViewedVersion
    );
    setUnreadCount(unreadUpdates.length);
  }, []);

  useEffect(() => {
    if (!showPopover && updates.length > 0) {
      const latestVersion = updates[0].version;
      const lastSeenVersion = localStorage.getItem('lastSeenVersion');
      
      if (!lastSeenVersion || lastSeenVersion !== latestVersion) {
        localStorage.setItem('lastSeenUpdateVersion', latestVersion);
      }
    }
  }, [showPopover]);

  const portalContent = showPopover && (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-25 z-[9999]"
        onClick={handleClose}
      />
      
      <div className="fixed top-20 right-4 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-[10000]">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">Latest Updates</h3>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {updates.map((update, index) => (
            <div key={index} className={`${index > 0 ? 'border-t border-gray-100 pt-3' : ''} mb-3`}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-blue-600">Update</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{update.date}</span>
                  <span className="text-xs text-gray-400">v{update.version}</span>
                </div>
              </div>
              <ul className="text-sm space-y-1">
                {update.changes.map((change, changeIndex) => (
                  <li key={changeIndex} className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const shouldShowNotification = () => {
    const lastSeenVersion = localStorage.getItem('lastSeenUpdateVersion');
    const latestVersion = updates[0]?.version;
    return latestVersion && (!lastSeenVersion || lastSeenVersion !== latestVersion);
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={handleViewUpdates}
        className="p-2 text-gray-600 hover:text-blue-600 rounded-full hover:bg-blue-50 transition-colors duration-200"
        title="View Updates"
      >
        <BellIcon className="h-6 w-6" />
        {shouldShowNotification() && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>
      
      {portalContent && createPortal(
        portalContent,
        document.getElementById('portal-root')
      )}
    </div>
  );
};

export default UpdatesPopover; 